import * as React from 'react';

import { union } from 'lodash';
import Q from 'q';
import { compose, setDisplayName } from 'recompose';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import redirect from 'js/lib/coursera.redirect';
import user from 'js/lib/user';

import withApiHandler from 'bundles/coursera-ui/components/hocs/withApiHandler';
import type { PropsFromWithApiHandler } from 'bundles/coursera-ui/components/hocs/withApiHandler';
import { ApiStates } from 'bundles/coursera-ui/constants/apiNotificationConstants';
import type { ApiState as ApiStatus } from 'bundles/coursera-ui/constants/apiNotificationConstants';
import ProgramAdminDenyModal from 'bundles/enterprise-legacy-learner-home/components/modals/ProgramAdminDenyModal';
import ProgramDenyModal from 'bundles/enterprise-legacy-learner-home/components/modals/ProgramDenyModal';
import ProgramEmailVerifyModal from 'bundles/enterprise-legacy-learner-home/components/modals/ProgramEmailVerifyModal';
import ProgramEmailVerifyModalWithoutProgramContext from 'bundles/enterprise-legacy-learner-home/components/modals/ProgramEmailVerifyModalWithoutProgramContext';
import ProgramJoinModal from 'bundles/enterprise-legacy-learner-home/components/modals/ProgramJoinModal';
import {
  AUTOENROLLED_COURSES_COOKIE_KEY,
  AUTOENROLLED_COURSES_COOKIE_PATH,
} from 'bundles/enterprise-legacy-learner-home/components/modals/constants';
import { PRODUCT_TYPES } from 'bundles/enterprise-legacy-learner-home/constants/ProgramActionConstants';
import { API_TAGS } from 'bundles/enterprise-legacy-learner-home/constants/ProgramHomeConstants';
import { type PROGRAM_JOIN_ERRORS as PROGRAM_JOIN_ERROR_TYPE } from 'bundles/enterprise-legacy-learner-home/constants/ProgramJoinModalErrors';
import ProgramActionApiManager from 'bundles/enterprise-legacy-learner-home/models/ProgramActionApiManager';
import type {
  EnterpriseProgramFromSlugFragment,
  NaptimeThirdPartyOrganizationFromSlugFragment,
} from 'bundles/enterprise-legacy-learner-home/queries/__generated__/EnterpriseProgramBySlugQuery';
import type { ProductCardCourseFragmentFragment as Course } from 'bundles/enterprise-legacy-learner-home/queries/__generated__/ProductCoursesQuery';
import { useProgramJoinMutation } from 'bundles/enterprise-legacy-learner-home/queryHooks/useProgramJoinMutation';
import { useResendVerificationEmail } from 'bundles/enterprise-legacy-learner-home/queryHooks/useResendVerificationEmail';
import type ProgramLearnerApiManager from 'bundles/enterprise-legacy-learner-home/utils/ProgramLearnerApiManager';
import storageUtils from 'bundles/enterprise-legacy-learner-home/utils/storageUtils';
import type { InjectedNaptime, NaptimeError } from 'bundles/naptimejs';
import Naptime from 'bundles/naptimejs';
import Modal from 'bundles/phoenix/components/Modal';
import type { ProgramMembershipType } from 'bundles/program-home/types/ProgramMembershipAdapter';
import useHasThirdPartyOrgEnforcedSSOLogin from 'bundles/saml-sso/shared/hooks/useHasThirdPartyOrgEnforcedSSOLogin';
import { useEnterpriseFeatureFlags } from 'bundles/saml-sso/shared/hooks/useProductFeatureFlags';
import { useEnterpriseModalManager } from 'bundles/unified-common/contexts/EnterpriseModalManagerContext';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import type { ProgramJoinErrorModalInfo } from 'bundles/unified-common/types/enterprise';

import 'css!./__styles__/ProgramAccessModal';

type PropsFromCaller = {
  apiManager: ProgramLearnerApiManager;
  isAccessModalDismissed: boolean;
  isProgramAdmin: boolean;
  onCloseAccessModal: () => void;
  program: EnterpriseProgramFromSlugFragment;
  programMembership?: ProgramMembershipType;
  reloadProgramMembership: () => void;
  router: InjectedRouter;
  thirdPartyOrganization: NaptimeThirdPartyOrganizationFromSlugFragment;
  userId: number;
  isUpswell: boolean;
  isNonBlacklistedC4cv: boolean;
  shouldUseJoinTrackingVerbiage?: boolean;
  isGwGProgram?: boolean;
  programRecommendationsAvailableCoursesCopy: boolean;
  autoEnrolledCoursesWithData?: Course[];
  openProgramJoinErrorModal?: (programJoinErrorInfo: ProgramJoinErrorModalInfo) => void;
};

type PropsFromNaptime = {
  naptime: InjectedNaptime;
};

type Props = PropsFromCaller & PropsFromWithApiHandler & PropsFromNaptime;

type PropsForApi = { apiStatus: ApiStatus; error?: NaptimeError | null } | undefined;

const CONFIG = {
  ProgramDenyModal: {
    'data-e2e': 'ProgramDenyModal',
    modalName: 'ProgramDenyModal',
    trackingName: 'program_deny_modal',
  },
  ProgramEmailVerifyModal: {
    'data-e2e': 'ProgramEmailVerifyModal',
    modalName: 'ProgramEmailVerifyModal',
    trackingName: 'program_email_verify_modal',
  },
  ProgramJoinModal: {
    'data-e2e': 'ProgramJoinModal',
    modalName: 'ProgramJoinModal',
    trackingName: 'program_join_modal',
  },
};

export function ProgramAccessModal({
  router,
  onCloseAccessModal,
  isAccessModalDismissed,
  apiTag,
  apiStatus,
  error,
  programMembership,
  userId,
  isGwGProgram,
  programRecommendationsAvailableCoursesCopy,
  autoEnrolledCoursesWithData,
  apiManager,
  handleApiPromise,
  reloadProgramMembership,
  isProgramAdmin,
  program,
  thirdPartyOrganization,
  naptime,
  openProgramJoinErrorModal,
  shouldUseJoinTrackingVerbiage = false,
}: Props) {
  const features = useEnterpriseProductFeatures();
  const modalManager = useEnterpriseModalManager();
  const enableProgramAutoJoin = features?.enableProgramAutoJoin ?? false;
  const programAutoJoinAttemptedRef = React.useRef(false);

  const shouldDenyAccess = programMembership?.isNotProgramMember;
  const showProgramDenyModal = shouldDenyAccess && !isProgramAdmin && !enableProgramAutoJoin;
  const showProgramAdminDenyModal = shouldDenyAccess && isProgramAdmin && !enableProgramAutoJoin;

  const showEmailVerificationModal = !showProgramDenyModal && programMembership?.isEmailNotVerified;
  const isAutoEnrolling = Boolean(router.location.query.deepLinkItemId || router.location.query.deepLinkModuleId);
  const [showProgramJoinModal, setShowProgramJoinModal] = React.useState<boolean>(false);
  const [autoAcceptProgramJoinModal, setAutoAcceptProgramJoinModal] = React.useState<boolean>(false);
  const { isExternal } = router.location.query;
  const showVerificationModalWithoutProgramContext =
    isExternal === 'true' &&
    (features?.enableOptimizedLearnerFlow || features?.enableOptimisedEnrollmentSetting) &&
    showEmailVerificationModal;

  const { isSsoLoginForced, isLoading: isSsoFlagLoading } = useHasThirdPartyOrgEnforcedSSOLogin(
    thirdPartyOrganization.id,
    user.get().email_address
  );
  const { isUnifiedSsoEnabled: isUnifiedSso } = useEnterpriseFeatureFlags(thirdPartyOrganization.id);

  const { programJoinMutation } = useProgramJoinMutation();
  const { resendVerificationEmail } = useResendVerificationEmail();

  // Axa handles program join automatically when they submit the assessments
  const canJoinProgram =
    !showProgramDenyModal && !showEmailVerificationModal && programMembership?.isEligibleToJoinProgram;

  const { hasModalToDisplay } = programMembership ?? {};
  const programId = program.id;
  const programSlug = program.slug;
  const programName = program.name;
  const orgName = thirdPartyOrganization?.name ?? 'company';
  const orgSlug = thirdPartyOrganization?.slug;
  const thirdPartyOrganizationId = thirdPartyOrganization.id;

  React.useEffect(() => {
    programAutoJoinAttemptedRef.current = false;
  }, [programId]);

  let emailVerifyProps: PropsForApi;
  let invitationJoinProps: PropsForApi = { apiStatus: ApiStates.API_BEFORE_SEND };

  if (apiTag === API_TAGS.RESEND_EMAIL) {
    emailVerifyProps = { apiStatus, error };
  } else if (apiTag === API_TAGS.JOIN_PROGRAM) {
    invitationJoinProps = { apiStatus, error };
  }
  React.useEffect(() => {
    if (enableProgramAutoJoin) {
      setShowProgramJoinModal(false);
      setAutoAcceptProgramJoinModal(false);
      return;
    }
    if (!isSsoFlagLoading) {
      const isAutoJoiningProgram = router.location.query.autoJoin === programId;
      const comingFromSsoLogin = (isSsoLoginForced || isUnifiedSso) && isAutoJoiningProgram;
      const shouldAutoAcceptProgramJoinModal = canJoinProgram && comingFromSsoLogin && !isAutoEnrolling;
      const shouldShowProgramJoinModal = canJoinProgram && (!comingFromSsoLogin || isAutoEnrolling || !!error);
      setShowProgramJoinModal(!!shouldShowProgramJoinModal);
      setAutoAcceptProgramJoinModal(!!shouldAutoAcceptProgramJoinModal);
    }
  }, [
    enableProgramAutoJoin,
    isSsoFlagLoading,
    isSsoLoginForced,
    isUnifiedSso,
    canJoinProgram,
    isAutoEnrolling,
    error,
    router.location.query.autoJoin,
    programId,
  ]);

  React.useEffect(() => {
    if (isAccessModalDismissed || !hasModalToDisplay) {
      onCloseAccessModal();
    }
  }, [isAccessModalDismissed, hasModalToDisplay, onCloseAccessModal]);

  let modalName: keyof typeof CONFIG = 'ProgramDenyModal';

  // Disable the Styleguide stuff when we're rendering a CDS modal.
  let wrapperClassName: string | undefined = 'vertical-box align-items-absolute-center text-xs-center';
  if (showEmailVerificationModal) {
    modalName = 'ProgramEmailVerifyModal';
    wrapperClassName = undefined;
  } else if (showProgramJoinModal) {
    modalName = 'ProgramJoinModal';
  }

  const onJoinProgram = React.useCallback<() => void>(async () => {
    if (userId) {
      if (autoEnrolledCoursesWithData && autoEnrolledCoursesWithData.length > 0) {
        const currentAutoenrolledCoursesCookieValue = storageUtils.get(
          userId,
          AUTOENROLLED_COURSES_COOKIE_PATH,
          AUTOENROLLED_COURSES_COOKIE_KEY,
          []
        );

        storageUtils.set(
          userId,
          AUTOENROLLED_COURSES_COOKIE_PATH,
          AUTOENROLLED_COURSES_COOKIE_KEY,
          union(
            autoEnrolledCoursesWithData.map((course) => course.id),
            currentAutoenrolledCoursesCookieValue
          )
        );
      }

      const { productId, productType, eoc, redirectPath } = router.location.query;

      const refreshAfterMutation = () => {
        if (enableProgramAutoJoin) {
          return;
        }
        // We cannot correctly invalidate all of the cache locations, so just reload the page as a workaround
        // until we sort out our data fetching throughput the bundle.
        window.location.reload();
      };

      const redirectAfterMutation = () => {
        // TODO: Change to react router method once Clips redesign is generally availaable
        // Right now, learners might be redirected from the old Clips version in video-logged-out-page which is not using the
        // same react router instance as the new Clips redesign in program-home
        redirect.setLocation(decodeURIComponent(redirectPath));
      };

      let onApiSuccess = () => {
        return redirectPath ? redirectAfterMutation() : refreshAfterMutation();
      };

      const hasNavFromEOCModal = Boolean(productId && productType && eoc);
      if (hasNavFromEOCModal) {
        // If we're joining via an EOC redirect, then also follow it up with an enrollment before opening the MiniXDP.
        onApiSuccess = () => {
          const api = new ProgramActionApiManager({ programId, userId, naptime });
          const enrollPromise =
            productType === PRODUCT_TYPES.SPECIALIZATION
              ? api.getEnrollInS12nPromise({ firstCourseId: null, s12nId: productId, collectionId: null })
              : api.getEnrollInCoursePromise({ courseId: productId, collectionId: null });
          handleApiPromise({
            apiPromise: enrollPromise,
            // This is used to pick which component to pipe props to, which is still the join modal.
            apiTag: API_TAGS.JOIN_PROGRAM,
            apiSuccessCallback: refreshAfterMutation,
          });
        };
      }

      try {
        const result = await programJoinMutation({ programId });

        if (result?.__typename === 'ProgramMembership_JoinResultSuccess') {
          onApiSuccess();
        } else if (result?.__typename === 'ProgramMembership_JoinResultFailure') {
          const errorCode = result.error as keyof typeof PROGRAM_JOIN_ERROR_TYPE;

          if (enableProgramAutoJoin) {
            modalManager?.setProgramAutoJoinErrorCode?.(errorCode ?? 'CONTRACT_NOT_AVAILABLE');
            return;
          } else if (autoAcceptProgramJoinModal && errorCode && openProgramJoinErrorModal) {
            openProgramJoinErrorModal({
              errorCode,
              showModal: true,
            });
          }
        }
      } catch (e) {
        if (enableProgramAutoJoin) {
          const code = e instanceof Error ? e.message : 'CONTRACT_NOT_AVAILABLE';
          modalManager?.setProgramAutoJoinErrorCode?.(code);
          return;
        } else if (autoAcceptProgramJoinModal && openProgramJoinErrorModal) {
          openProgramJoinErrorModal({
            errorCode: 'CONTRACT_NOT_AVAILABLE',
            showModal: true,
          });
        } else if (e?.message === 'CREATE_MEMBERSHIP_USER_LIMIT_ERROR' && openProgramJoinErrorModal) {
          openProgramJoinErrorModal({
            errorCode: e.message,
            showModal: true,
          });
        }
      }
    }
  }, [
    userId,
    autoEnrolledCoursesWithData,
    programId,
    router.location.query,
    autoAcceptProgramJoinModal,
    openProgramJoinErrorModal,
    programJoinMutation,
    handleApiPromise,
    naptime,
    enableProgramAutoJoin,
    modalManager,
  ]);

  React.useEffect(() => {
    if (!enableProgramAutoJoin && autoAcceptProgramJoinModal && apiStatus === ApiStates.API_BEFORE_SEND) {
      onJoinProgram();
    }
  }, [enableProgramAutoJoin, autoAcceptProgramJoinModal, apiStatus, onJoinProgram]);

  // Same eligibility as ProgramJoinModal: canJoinProgram => invited | allowlisted | soft-deleted member with verified email.
  React.useEffect(() => {
    if (!enableProgramAutoJoin || !canJoinProgram || isAutoEnrolling) {
      return;
    }
    if (programAutoJoinAttemptedRef.current) {
      return;
    }
    programAutoJoinAttemptedRef.current = true;
    onJoinProgram();
  }, [enableProgramAutoJoin, canJoinProgram, isAutoEnrolling, onJoinProgram, programAutoJoinAttemptedRef]);

  const onResendEmail = React.useCallback<() => Promise<void>>(async () => {
    if (programMembership && userId) {
      const redirectUrl = window.location.pathname;
      await resendVerificationEmail({
        programId,
        userId,
        redirectUrl,
      });
    }
  }, [programMembership, userId, programId, resendVerificationEmail]);
  const onCheckVerified = () => {
    const apiPromise = Q.all([reloadProgramMembership(), apiManager.getRefreshDataPromise(['programMemberships.v2'])]);
    handleApiPromise({
      apiPromise,
      apiTag: API_TAGS.RESEND_EMAIL,
    });
  };
  const onDenyModalBrowseClick = (apiSuccessCallback: $TSFixMe) => {
    if (userId) {
      const apiPromise = apiManager.setSwitcher({
        userId,
        selectionType: 'COURSERA',
      });
      handleApiPromise({ apiPromise, apiSuccessCallback, apiErrorCallback: apiSuccessCallback });
      // we still want to redirect them back even though it fails
    }
  };

  const shouldHideModals =
    (!showProgramDenyModal && !showProgramAdminDenyModal && !showEmailVerificationModal && !showProgramJoinModal) ||
    isAccessModalDismissed;

  if (shouldHideModals) {
    return null;
  }

  if (showVerificationModalWithoutProgramContext) {
    return (
      <ProgramEmailVerifyModalWithoutProgramContext
        onClose={onCloseAccessModal}
        onResendEmail={onResendEmail}
        onCheckVerified={onCheckVerified}
        emailAddress={programMembership.externalUserData.definition.email}
        {...emailVerifyProps}
      />
    );
  }

  return (
    <div className="rc-ProgramAccessModal">
      <Modal {...CONFIG[modalName]} data={{ programId, programName }} handleClose={onCloseAccessModal}>
        <div className={wrapperClassName}>
          {showProgramDenyModal && (
            <ProgramDenyModal
              programId={programId}
              programName={programName}
              thirdPartyOrganizationId={thirdPartyOrganizationId}
              onBrowse={onDenyModalBrowseClick}
            />
          )}
          {showProgramAdminDenyModal && (
            <ProgramAdminDenyModal
              programSlug={programSlug}
              orgName={orgName}
              orgSlug={orgSlug}
              onClose={onCloseAccessModal}
            />
          )}
          {showEmailVerificationModal && !showVerificationModalWithoutProgramContext && (
            <ProgramEmailVerifyModal
              programName={programName}
              onClose={onCloseAccessModal}
              onResendEmail={onResendEmail}
              onCheckVerified={onCheckVerified}
              programMembership={programMembership}
              {...emailVerifyProps}
            />
          )}
          {!enableProgramAutoJoin && showProgramJoinModal && (
            <ProgramJoinModal
              programId={programId}
              programSlug={programSlug}
              programName={programName}
              programMembership={programMembership}
              thirdPartyOrganizationSlug={orgSlug}
              thirdPartyOrganizationId={thirdPartyOrganizationId}
              orgName={orgName}
              onJoinProgram={onJoinProgram}
              shouldUseJoinTrackingVerbiage={shouldUseJoinTrackingVerbiage}
              router={router}
              isGwGProgram={isGwGProgram}
              programRecommendationsAvailableCoursesCopy={programRecommendationsAvailableCoursesCopy}
              autoEnrolledCoursesWithData={autoEnrolledCoursesWithData}
              userId={userId}
              isAutoEnrolling={isAutoEnrolling}
              {...invitationJoinProps}
            />
          )}
        </div>
      </Modal>
    </div>
  );
}

export default compose<Props, PropsFromCaller>(
  setDisplayName('ProgramAccessModalHOC'),
  withApiHandler({}),
  Naptime.createContainer(() => ({})) // Inject a naptime instance for onJoinProgram.
)(ProgramAccessModal);
